import * as _ from 'lodash-es';
import React, { useState } from 'react';
import Badge from '../components/Badge';
import ConfirmationModal from '../components/ConfirmationModal';
import * as styles from './SectionChangesConfirmationModal.module.scss';
import SidebarSectionIcon from './SidebarSectionIcon';
import type { Section } from './SidebarSectionsProvider';
import { getSidebarSectionNameWithFallback } from './SidebarSectionsProvider';

function getDisplayAction(section: Section) {
  const lastAction = section.actions?.at(-1);
  if (lastAction === 'delete') return lastAction;
  const firstAction = section.actions?.at(0);
  if (firstAction === 'create') return firstAction;

  if (section.actions?.includes('update')) {
    return 'update';
  }

  if (section.actions?.includes('hide') || section.actions?.includes('show')) {
    return _.findLast(
      section.actions,
      (action) => action === 'hide' || action === 'show',
    );
  }

  return section.actions?.at(-1);
}

interface SectionRowProps {
  section: Section;
}

const SectionRow = ({ section }: SectionRowProps) => {
  const action = getDisplayAction(section);

  const color = (() => {
    if (action === 'delete') return 'red';
    if (action === 'create') return 'green';
    return 'blue';
  })();

  return (
    <div className={styles.sectionRow}>
      <SidebarSectionIcon section={section} />
      <span className={styles.sectionName}>
        {getSidebarSectionNameWithFallback(section.name)}
      </span>
      <Badge color={color}>{action}</Badge>
    </div>
  );
};

const CONFIRMATION_MODAL_TYPES = {
  DISCARD: {
    title: 'Discard your changes?',
    message: 'The changes you made to sidebar sections have not been saved:',
    action: 'Discard',
  },
  SAVE: {
    title: 'Finalize your changes?',
    message: 'The changes that you made to sidebar sections will be saved:',
    action: 'Save changes',
  },
} as const;

interface DiscardSectionChangesModalContentProps {
  type: keyof typeof CONFIRMATION_MODAL_TYPES;
  saving: boolean;
  onConfirm: () => void;
  onBack: () => void;
  unsavedSections: Section[];
}

const SectionChangesConfirmationModal = ({
  type,
  saving,
  onConfirm,
  onBack,
  unsavedSections,
}: DiscardSectionChangesModalContentProps) => {
  const { title, message, action } = CONFIRMATION_MODAL_TYPES[type];

  // Freeze modal content while saving. Otherwise, we might have flickering
  // after getting partial updates from GraphQL subscriptions.
  const [localUnsavedSections, setLocalUnsavedSections] = useState<
    Section[] | null
  >(null);

  if (saving && !localUnsavedSections) {
    setLocalUnsavedSections(unsavedSections);
  } else if (!saving && localUnsavedSections) {
    setLocalUnsavedSections(null);
  }

  return (
    <ConfirmationModal
      close={onBack}
      title={title}
      cancelButtonText="Back"
      confirmButton={{ text: action, onClick: onConfirm, loading: saving }}
      description={message}
      width={448}
    >
      <div className={styles.sectionRowContainer}>
        {(localUnsavedSections ?? unsavedSections).map((section) => (
          <SectionRow section={section} key={section.id} />
        ))}
      </div>
    </ConfirmationModal>
  );
};

export default SectionChangesConfirmationModal;
